import AccordionCustom from "@components/Accordion/AccordionCustom";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import FaqHeader from "@components/LandingPageComponents/RazorPayComponents/FaqHeader";
import RazorHeader from "@components/LandingPageComponents/RazorPayComponents/RazorHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import dynamic from "next/dynamic";
import Head from "next/head";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/razorpay/razorpay-card1.png",
        title: "Sync with RazorPay in seconds",
        description: "Log into your RazorPay account and connect it with Gallabox in a few simple clicks, and instantly enable safe and seamless payments on WhatsApp.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/razorpay/razorpay-card2.png",
        title: "Generate payment links",
        description:
            "Generate payment links instantly within your compose box during a conversation on WhatsApp, and let Gallabox automagically handle the payment notifications and acknowledgment flows.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/razorpay/razorpay-card3.png",
        title: "Collect invoice payments with ease",
        description: "Collect payments faster via WhatsApp by integrating RazorPay with your invoicing systems such as QuickBooks, Zoho Books, and more.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/razorpay/razorpay-card4.png",
        title: "Automate service bookings",
        description: "Generate payment links dynamically on WhatsApp for your service bookings, whether handled by a bot or as part of a campaign sequence.",
    },
];

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "Do I need coding knowledge for RazorPay WhatsApp Integration?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Not at all. This is where Gallabox makes it super easy. You can integrate RazorPay and WhatsApp in minutes with just a few clicks and start sharing payment links on WhatsApp.",
            },
        },
        {
            "@type": "Question",
            name: "Is RazorPay for WhatsApp integration secure?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes! Integrating RazorPay with Gallabox is a secure option for automating payments on WhatsApp. Further, WhatsApp uses encryption and follows security protocols and industry best practices to ensure your data is secured while being stored or transferred.",
            },
        },
        {
            "@type": "Question",
            name: "What can I use RazorPay WhatsApp integration for?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "By integrating RazorPay and WhatsApp, you can automate end-to-end payment workflow, i.e., from payment reminders to acknowledgments to generating invoices to managing refunds, without compromising on customer experience.",
            },
        },
    ],
};

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const ImageGrid = dynamic(() => import("@components/ImageGrid"), {
    ssr: false,
});

const razorpayLanding: React.FC = () => {
    return (
        <NewLayout
            title="Razorpay WhatsApp Integration | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/razorpay/Razorpay-og.jpg" }}
            canonical="https://gallabox.com/whatsapp-integrations/razorpay"
            description="Send automated payment alerts via Razorpay WhatsApp integration. Keep customers updated on transactions and invoices."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <RazorHeader />

            <div className="lg:max-w-7xl mx-auto">
                <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                <ClientLogos />
            </div>
            <CardDataCarousal cardDataCollection={cardDataCollection} />
            <FeatureCardSection
                subTitle="One-click Payment Links"
                title="Convert engaging prospects to paying customers"
                imageURl="https://assets.gallabox.com/gb-home/razorpay/razorpay-onclick.png"
                description="Go from engaging in WhatsApp conversations to closing deals without switching between payment apps. Generate a Razorpay payment link instantly and share it with your prospects when they are ready to buy."
                isImageRightSide
            />
            <FeatureCardSection
                subTitle="Payment Workflows"
                imageURl="https://assets.gallabox.com/gb-home/razorpay/razorpay-payments.png"
                title="Say goodbye to late payments and frustrating collection efforts"
                description="Automate your end-to-end payment workflow, be it payment reminders, acknowledgments, generating invoices or managing refunds—all without compromising the customer experience."
            />
            <FeatureCardSection
                imageURl="https://assets.gallabox.com/gb-home/razorpay/razorpay-catalogs.png"
                subTitle="Catalogs for E-commerce"
                title="Reduce cart abandonment. Increase revenue."
                description="Offer a whole new level of convenience and interactivity to eCommerce. Reduce cart abandonment, and engage your customers better by integrating your product catalog into WhatsApp with a Pay Now button."
                isImageRightSide
            />
            <YellowCTAhref customText="Automate and simplify payments on WhatsApp today!" />
            <TestimonialSection
                TestimonialSectionPropsData={[
                    {
                        companyImg: "https://assets.gallabox.com/gb-home/travelmite-logo.svg",
                        avatarImg: "https://assets.gallabox.com/gb-home/travelmite-comp-logo.png",
                        headerTitle: "Gallabox solves our pain points, such as unattended customer & agent chat visibility",
                        nameText: "Vaseem Ahmed",
                        professionText: "Founder & CEO, Travelmite",
                    },
                    {
                        companyImg: "https://assets.gallabox.com/gb-home/estr-logo.png",
                        avatarImg: "https://assets.gallabox.com/gb-home/estr-owner.png",
                        headerTitle: "The integration process is incredibly straightforward, with no hassles whatsoever. Furthermore, the support provided is excellent.",
                        nameText: "Rohit Shukla",
                        professionText: "Founder, ESTR Holidays",
                    },
                ]}
            />
            <ImageGrid />
            <YellowCTAhref customText="Automate and simplify payments on WhatsApp today!" buttonText="Get started Today" />
            <FaqHeader />
            <AccordionCustom
                title="Razorpay Integration"
                items={[
                    {
                        question: "Do I need coding knowledge for RazorPay WhatsApp Integration?",
                        answer: "Not at all. This is where Gallabox makes it super easy. You can integrate RazorPay and WhatsApp in minutes with just a few clicks and start sharing payment links on WhatsApp.",
                    },
                    {
                        question: "Is RazorPay for WhatsApp integration secure?",
                        answer: "Yes! Integrating RazorPay with Gallabox is a secure option for automating payments on WhatsApp. Further, WhatsApp uses encryption and follows security protocols and industry best practices to ensure your data is secured while being stored or transferred.",
                    },
                    {
                        question: "What can I use RazorPay WhatsApp integration for?",
                        answer: "By integrating RazorPay and WhatsApp, you can automate end-to-end payment workflow, i.e., from payment reminders to acknowledgments to generating invoices to managing refunds, without compromising on customer experience.",
                    },
                ]}
            />
        </NewLayout>
    );
};

export default razorpayLanding;
